// Der Claude-Stand aus dem Homelab-Protokoll (Topics claude.lage, claude.chat,
// claude.commits, claude.repo; internal/api/claude_protokoll.go,
// docs/protokoll.md › „Claude über das Protokoll“) -- die reine Rechnung, ohne
// Runen und ohne Laufzeit-Importe, damit node (protokoll.test.mjs) sie laden
// kann. Den Store hält claude-summary.svelte.ts.
//
//   claude.lage                 alles außer chats und repo_staende, dazu
//                               verbunden; chats = Ids, repo_staende = Repo-Ids,
//                               beide in der Reihenfolge der REST-Antwort
//   claude.chat.<id>.stand      ein Chat ohne seine Commits
//   claude.chat.<id>.commits    seine Commits (null = keine)
//   claude.repo.<id>.stand      ein Repo-Stand, Id = repoId(Pfad)
//
// Jede Instanz trägt ihren Teil als JSON-Text im Feld `json`.

import type { Chat, ClaudeAntwort, Commit, RepoStand } from "./claude";

export type Ort =
  | { art: "lage" }
  | { art: "chat"; id: string }
  | { art: "commits"; id: string }
  | { art: "repo"; id: string };

export function instanzOrt(inst: string): Ort | undefined {
  if (inst === "claude.lage") return { art: "lage" };
  const t = inst.split(".");
  if (t.length !== 4 || t[0] !== "claude" || !t[2]) return undefined;
  if (t[1] === "chat" && t[3] === "stand") return { art: "chat", id: t[2] };
  if (t[1] === "chat" && t[3] === "commits") return { art: "commits", id: t[2] };
  if (t[1] === "repo" && t[3] === "stand") return { art: "repo", id: t[2] };
  return undefined;
}

export function ortSchluessel(o: Ort): string {
  return o.art === "lage" ? "lage" : `${o.art}:${o.id}`;
}

/** Instanz-Id eines Repo-Stands wie internal/api claudeRepoID: jedes Byte
 *  (UTF-8) außer a-z, A-Z, 0-9 und "-" wird "_" + zwei Hex-Ziffern. */
export function repoId(repo: string): string {
  let out = "";
  for (const b of new TextEncoder().encode(repo)) {
    const c = String.fromCharCode(b);
    out += /[a-zA-Z0-9-]/.test(c) ? c : "_" + b.toString(16).padStart(2, "0");
  }
  return out;
}

/** Taugt eine Chat-Id als Segment? Andere setzt der Dienst nicht. */
export function gueltigeId(id: string): boolean {
  return /^[a-zA-Z0-9_-]+$/.test(id);
}

/** Der JSON-Text einer Instanz; undefined, wenn er fehlt oder nicht lesbar ist. */
export function liesJson(daten: unknown): { roh: string; wert: unknown } | undefined {
  const roh = (daten as { json?: unknown } | null)?.json;
  if (typeof roh !== "string") return undefined;
  try {
    return { roh, wert: JSON.parse(roh) };
  } catch {
    return undefined;
  }
}

/** Die Teile, wie sie zuletzt kamen. */
export interface Teile {
  lage?: Record<string, unknown>;
  chats: Map<string, Chat>;
  commits: Map<string, Commit[]>;
  repos: Map<string, RepoStand>;
}

export function leereTeile(): Teile {
  return { chats: new Map(), commits: new Map(), repos: new Map() };
}

/** Die Antwort aus den Teilen; undefined, solange die Lage fehlt. Chats und
 *  Repo-Stände in der Reihenfolge der Lage, nur die, die schon da sind. */
export function standAus(t: Teile): ClaudeAntwort | undefined {
  if (!t.lage) return undefined;
  const { verbunden, chats, repo_staende, ...rest } = t.lage;
  const chatIds = Array.isArray(chats) ? (chats as string[]) : [];
  const repoIds = Array.isArray(repo_staende) ? (repo_staende as string[]) : [];
  const stand = {
    ...rest,
    chats: chatIds
      .map((id) => {
        const c = t.chats.get(id);
        if (!c) return undefined;
        const commits = t.commits.get(id);
        return commits && commits.length > 0 ? { ...c, commits } : c;
      })
      .filter((c): c is Chat => !!c),
    repo_staende: repoIds.map((id) => t.repos.get(id)).filter((r): r is RepoStand => !!r),
  };
  return { verbunden: verbunden === true, stand: stand as unknown as ClaudeAntwort["stand"] };
}

/** Zerlegt eine Antwort (REST, Ablage) in ihre Teile. */
export function teileAus(a: ClaudeAntwort): Teile {
  const t = leereTeile();
  const { chats, repo_staende, ...rest } = a.stand;
  const ids: string[] = [];
  for (const c of chats ?? []) {
    if (!gueltigeId(c.id)) continue;
    ids.push(c.id);
    const { commits, ...ohne } = c;
    t.chats.set(c.id, ohne as Chat);
    if (commits && commits.length > 0) t.commits.set(c.id, commits);
  }
  const repos: string[] = [];
  for (const r of repo_staende ?? []) {
    const id = repoId(r.repo);
    repos.push(id);
    t.repos.set(id, r);
  }
  t.lage = { ...rest, chats: ids, repo_staende: repos, verbunden: a.verbunden };
  return t;
}

/** Setzt einen Teil; Commits `null` (keine) löschen sie. */
export function setze(t: Teile, o: Ort, wert: unknown): void {
  switch (o.art) {
    case "lage":
      if (wert && typeof wert === "object") t.lage = wert as Record<string, unknown>;
      return;
    case "chat":
      if (wert && typeof wert === "object") t.chats.set(o.id, wert as Chat);
      return;
    case "commits":
      if (Array.isArray(wert) && wert.length > 0) t.commits.set(o.id, wert as Commit[]);
      else t.commits.delete(o.id);
      return;
    case "repo":
      if (wert && typeof wert === "object") t.repos.set(o.id, wert as RepoStand);
      return;
  }
}

export function entferne(t: Teile, o: Ort): void {
  switch (o.art) {
    case "lage":
      t.lage = undefined;
      return;
    case "chat":
      t.chats.delete(o.id);
      return;
    case "commits":
      t.commits.delete(o.id);
      return;
    case "repo":
      t.repos.delete(o.id);
      return;
  }
}

/**
 * Mischt eine REST-Antwort ein. `neuer(schluessel)` sagt, ob das Protokoll den
 * Teil geändert hat, seit der Abruf lief -- dann bleibt der Protokollstand
 * (sonst überschriebe eine ältere Antwort einen frischeren Delta). Was REST
 * nicht mehr führt, fällt weg.
 */
export function mischeRest(t: Teile, rest: ClaudeAntwort, neuer: (schluessel: string) => boolean): void {
  const r = teileAus(rest);
  if (!neuer("lage")) t.lage = r.lage;
  const mische = <T>(art: "chat" | "commits" | "repo", ziel: Map<string, T>, quelle: Map<string, T>): void => {
    for (const [k, v] of quelle) if (!neuer(`${art}:${k}`)) ziel.set(k, v);
    for (const k of [...ziel.keys()]) if (!quelle.has(k) && !neuer(`${art}:${k}`)) ziel.delete(k);
  };
  mische("chat", t.chats, r.chats);
  mische("commits", t.commits, r.commits);
  mische("repo", t.repos, r.repos);
}
